import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Plus, ScanLine, Share2 } from "lucide-react";
import { Outfit, fileUrl, outfitsApi, timeAgo } from "@/lib/catalog-api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function Outfits() {
  const [outfits, setOutfits] = useState<Outfit[] | null>(null);

  useEffect(() => {
    outfitsApi.list().then(setOutfits).catch(() => setOutfits([]));
  }, []);

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="outfits-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="text-[11px] uppercase tracking-[0.28em] text-oxblood font-medium mb-2">— Lookbooks</p>
          <h1 className="font-display text-3xl sm:text-4xl">Outfits</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-prose">
            Build pull lists for fittings, shoots, and red carpet. Drag pieces in,
            re-order, share with your client.
          </p>
        </div>
        <Button asChild data-testid="outfits-new-btn"
          className="bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-10 px-5">
          <Link to="/app/outfits/new"><Plus className="w-4 h-4 mr-2" />Build outfit</Link>
        </Button>
      </div>

      {outfits === null ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="outfits-loading">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="aspect-[4/3]" />)}
        </div>
      ) : outfits.length === 0 ? (
        <div className="border border-border bg-bone/30 p-12 text-center" data-testid="outfits-empty">
          <ScanLine className="w-6 h-6 mx-auto text-oxblood" strokeWidth={1.4} />
          <p className="font-display text-2xl mt-4">No outfits yet.</p>
          <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
            Pull together your first look. Designed for the moment a director asks
            for "three options, one fitting, twenty minutes."
          </p>
          <Button asChild className="mt-6 bg-oxblood hover:bg-oxblood-deep text-ivory rounded-none h-11 px-6"
            data-testid="outfits-empty-cta">
            <Link to="/app/outfits/new"><Plus className="w-4 h-4 mr-2" />Build your first outfit</Link>
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="outfits-grid">
          {outfits.map((o) => <OutfitCard key={o._id} outfit={o} />)}
        </div>
      )}
    </main>
  );
}

function OutfitCard({ outfit }: { outfit: Outfit }) {
  const cover = outfit.cover_photo_url ? fileUrl(outfit.cover_photo_url) : undefined;
  const additionalThumbs = outfit.items.slice(1, 4);
  return (
    <Link
      to={`/app/outfits/${outfit._id}`}
      data-testid={`outfit-card-${outfit._id}`}
      className="group block bg-background border border-border hover:border-oxblood/40 transition-colors"
    >
      <div className="aspect-[4/3] relative bg-bone overflow-hidden">
        {cover ? (
          <img src={cover} alt={outfit.name} loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
        ) : (
          <div className="w-full h-full grid place-items-center text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            No cover
          </div>
        )}
        <div className="absolute bottom-2 right-2 flex -space-x-1.5">
          {additionalThumbs.map((it) => (
            <span key={it.item_id} className="w-7 h-7 border border-ivory bg-bone overflow-hidden">
              {it.primary_photo_url ? (
                <img src={fileUrl(it.primary_photo_url)} alt={it.name}
                  className="w-full h-full object-cover" loading="lazy" />
              ) : null}
            </span>
          ))}
          {outfit.items.length > 4 && (
            <span className="w-7 h-7 border border-ivory bg-charcoal text-ivory text-[10px] grid place-items-center font-medium">
              +{outfit.items.length - 4}
            </span>
          )}
        </div>
      </div>
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {outfit.occasion || "Lookbook"}
        </p>
        <p className="font-display text-lg leading-snug line-clamp-1 mt-1">{outfit.name}</p>
        <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
          <span>{outfit.items.length} piece{outfit.items.length === 1 ? "" : "s"}</span>
          {outfit.scheduled_for ? (
            <span className="flex items-center gap-1">
              <CalendarClock className="w-3.5 h-3.5" />
              {new Date(outfit.scheduled_for).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            </span>
          ) : (
            <span>{timeAgo(outfit.updated_at)}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export { Share2 as _ShareIcon };
